import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager';
import { useSession } from '@marriott/mi-headless-utils';
import { StyledMerchandisingSmallBannerUpgraded } from './MerchandisingSmallBanner.styles';
import {
  IMerchandisingSmallBannerImage,
  IMerchandisingSmallBannerProps,
  MerchandisingSmallBannerV3Props,
  VariationTypes,
} from './MerchandisingSmallBanner.types';
import { returnDecodedString } from '../../utils/helpers';
import { AEMSkeletonLoaderStyles } from '../../skeleton-loader.styles';

const MerchandisingSmallBannerV3 = dynamic(() =>
  import('../../molecules/MerchandisingSmallBannerV3').then(mod => mod.MerchandisingSmallBannerV3)
);

const MerchandisingSmallBannerV1 = dynamic(() =>
  import('../../molecules/MerchandisingSmallBannerV1').then(mod => mod.MerchandisingSmallBannerV1)
);

const MerchandisingSmallBannerV2 = dynamic(() =>
  import('../../molecules/MerchandisingSmallBannerV2').then(mod => mod.MerchandisingSmallBannerV2)
);
export const MerchandisingSmallBannerComponent = (props: IMerchandisingSmallBannerProps) => {
  const { mboxParameter } = props;
  const {
    isTargetContentLoaded = mboxParameter && !AuthoringUtils.isInEditor() ? false : true,
    isTargetContentError = false,
    colorScheme = 'color-scheme1',
    logo,
    image,
    variation,
    customImageClass,
    headerTag,
    tertiaryImage,
    headerMedium,
    headerLong,
    legalLong,
    descriptionLong,
    descriptionMedium,
    descriptionLongMobile,
    customDescriptionClass,
    openInNewTab,
    ctaType,
    destinationUrl1,
    callToActionText1,
    trackingProperties,
    styleclass = '',
    componentId,
    openInNewWindowAriaLabel = '',
    customContainerClass = '',
    metaData,
    mathLabel1 = '',
    mathLabel2 = '',
    mathLabel3 = '',
    mathTitle = '',
    mathValue = '',
    mathType = '',
    isAuthor,
    displayEvergreenContent,
    preview_prop_currency_type = '', // this prop will be used by offers preview components, that only when there is no dataLayer[prop_currency_type] key
  }: IMerchandisingSmallBannerProps = props;

  const { datalayer } = useSession();
  const [variationDetails, setVariationsDetails] = useState({
    isVariationsAvailable: false,
    isPointsVariation: false,
    isCurrencyVariation: false,
    isNightsVariation: false,
  });

  useEffect(() => {
    if (
      isTargetContentLoaded &&
      ((mathType?.toLowerCase() === 'currency' &&
        (!!datalayer?.['prop_currency_type'] || !!preview_prop_currency_type)) ||
        mathType?.toLowerCase() === 'points' ||
        mathType?.toLowerCase() === 'nights')
    ) {
      setVariationsDetails({
        isVariationsAvailable: true,
        isPointsVariation: mathType?.toLowerCase() === 'points',
        isCurrencyVariation:
          mathType?.toLowerCase() === 'currency' &&
          (!!datalayer?.['prop_currency_type'] || !!preview_prop_currency_type),
        isNightsVariation: mathType?.toLowerCase() === 'nights',
      });
    }
  }, [isTargetContentLoaded, mathType]);

  const imageProps: IMerchandisingSmallBannerImage = {
    variation,
    logo,
    image,
    customImageClass,
    isTargetContentLoaded,
    isCobrand:
      isTargetContentError || !props.mboxParameter
        ? trackingProperties?.merchandisingCategory === 'cobrandNonPointsMath'
        : metaData?.type === 'CoBrand',
    isVariationsAvailable: variationDetails.isVariationsAvailable,
  };

  const descriptionProps = {
    descriptionLong: returnDecodedString(descriptionLong),
    descriptionLongMobile,
    customDescriptionClass,
  };

  const buttonProps = {
    ctaType,
    openInNewTab,
    destinationUrl1,
    callToActionText1,
    openInNewWindowAriaLabel,
    metaData,
    trackingProperties,
    isTargetContentError,
    mboxParameter,
    isAuth: datalayer?.['mr_prof_authentication_state'] === 'authenticated',
    isVariationsAvailable: variationDetails.isVariationsAvailable,
    targetData: props,
    isCobrand:
      isTargetContentError || !props.mboxParameter
        ? trackingProperties?.merchandisingCategory === 'cobrandNonPointsMath'
        : metaData?.type === 'CoBrand',
  };

  const variationProps = {
    currencyType: (datalayer?.['prop_currency_type'] || preview_prop_currency_type) as string,
    currencyTotalStayAmount: parseFloat(`${datalayer?.['res_revenue_total_locale'] ?? '0'}`),
    pointsTotalStayAmount: parseInt(`${datalayer?.['mr_prof_points_balance'] ?? '0'}`, 10),
    currentEliteNightCredits: parseInt(`${datalayer?.['mr_prof_nights_booked_this_year'] ?? '0'}`, 10),
    isPointsVariation: variationDetails.isPointsVariation,
    isCurrencyVariation: variationDetails.isCurrencyVariation,
    isNightsVariation: variationDetails?.isNightsVariation,
    mathLabel1,
    mathLabel2,
    mathValue,
    mathLabel3,
    mathTitle,
  };

  const commonProps = {
    isTargetContentError,
    isTargetContentLoaded,
    mathType,
    legalLong,
    customContainerClass,
    colorScheme,
    headerTag,
    headerLong: returnDecodedString(headerLong),
    isVariationsAvailable: variationDetails.isVariationsAvailable,
  };

  const renderComponentByVariation = () => {
    switch (variation) {
      case VariationTypes.SmallBannerV3:
        return (
          <MerchandisingSmallBannerV3
            {...({
              isTargetContentLoaded,
              colorScheme,
              headerMedium,
              descriptionMedium,
              tertiaryImage,
              buttonProps,
              logo,
              image,
            } as unknown as MerchandisingSmallBannerV3Props)}
          />
        );

      case VariationTypes.SmallBannerV2:
        return (
          <MerchandisingSmallBannerV2
            {...({
              ...commonProps,
              imageProps,
              descriptionProps,
              buttonProps,
              variationProps,
            } as unknown as IMerchandisingSmallBannerProps)}
          />
        );

      default:
        return (
          <MerchandisingSmallBannerV1
            {...({
              ...commonProps,
              imageProps,
              descriptionProps,
              buttonProps,
              variationProps,
            } as unknown as IMerchandisingSmallBannerProps)}
          />
        ); //existing small banner is v1 variation
    }
  };

  if (isAuthor && String(displayEvergreenContent) !== 'true') {
    return (
      <AEMSkeletonLoaderStyles>
        <div className="skeleton-loader aem-skeleton-loader"></div>
      </AEMSkeletonLoaderStyles>
    );
  }

  return (
    <div
      data-component-name="o-merch-MerchandisingSmallBanner"
      data-testid="MerchandisingSmallBanner"
      className={clsx({
        'd-none': isTargetContentError && String(displayEvergreenContent) === 'false',
      })}
      style={{ clear: 'both' }}
    >
      <StyledMerchandisingSmallBannerUpgraded
        id={componentId}
        className={clsx('position-relative pt-md-5 pb-4 pb-md-5 mt-md-0', styleclass)}
      >
        <div className={clsx('container', variation == VariationTypes.SmallBannerV2 ? 'pl-md-5 pr-md-0' : 'px-0')}>
          {renderComponentByVariation()}
        </div>
      </StyledMerchandisingSmallBannerUpgraded>
    </div>
  );
};

const MerchandisingSmallBannerConfig = {
  emptyLabel: 'Merchandising Small Banner',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/merchandisingsmallbanner`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const MerchandisingSmallBanner = (props: any) => {
  return (
    <EditableComponent
      config={MerchandisingSmallBannerConfig}
      {...props}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      children={<MerchandisingSmallBannerComponent {...({} as any)} />}
    />
  );
};
